import axios from 'axios';
import {DEFAULT_RHYME_TYPE, getRhymeType} from './rhymeTypes';

// Legacy endpoint: a Cloud Function that forwards to the old, credentialed
// API. Its URL is pinned -- redeploying via the Firebase CLI instead of
// `gcloud functions deploy rhyme-proxy --gen2 --region=us-central1` would mint
// a new URL and break this.
const PROXY_URL = 'https://us-central1-double-rhyme-website.cloudfunctions.net/rhyme-proxy';

// Build-time switch to the new credential-free backend, e.g.
//   REACT_APP_RHYME_API=https://api.double-rhyme.com npm run build
// When set, the site calls `${base}/v3/rhymes` directly (CORS `*`, same JSON
// envelope as the proxy). When unset, the proxy above stays in use, so a plain
// `npm run build` deploys exactly what ran before.
const RHYME_API_BASE = (process.env.REACT_APP_RHYME_API || '').trim().replace(/\/+$/, '');

export const API_URL = RHYME_API_BASE ? `${RHYME_API_BASE}/v3/rhymes` : PROXY_URL;

const REQUEST_TIMEOUT_MS = 15000;

// Same cap the proxy enforced. The backend accepts far longer terms, which only
// ever produce "No rhymes found" and bloat the shareable ?s= link.
export const MAX_SEARCHTERM_LENGTH = 30;

// The API only knows the "rap" genre for German and English and answers 422
// for genre=rap in any other language.
export const RAP_GENRE_LANGUAGES = ['de', 'en'];

export function genreFor(language) {
  return RAP_GENRE_LANGUAGES.includes(language) ? 'rap' : 'normal';
}

export function getRhymes(word, language, rhymeTypeId = DEFAULT_RHYME_TYPE) {
  if ((word || '').trim().length > MAX_SEARCHTERM_LENGTH) {
    return Promise.reject(new Error(`Search term too long (max ${MAX_SEARCHTERM_LENGTH} characters)`));
  }
  const params = {
    searchterm: word,
    language: language,
    matchType: getRhymeType(rhymeTypeId).matchType,
    genre: genreFor(language)
  };

  return axios.get(API_URL, { params, timeout: REQUEST_TIMEOUT_MS })
    .then(response => response.data)
    .catch(error => {
      // The proxy answers 400/502 and the new backend 422, both with
      // {error: "..."}, for a too-long term, an unknown language, etc. Surface
      // that text rather than letting every failure collapse into
      // "No rhymes found".
      const message =
        error.response?.data?.error ||
        (error.code === 'ECONNABORTED' ? 'The search timed out. Please try again.' : null) ||
        'Could not reach the rhyme service. Please try again.';
      const wrapped = new Error(message);
      wrapped.cause = error;
      throw wrapped;
    });
}
